import { useEffect } from "react";
import { Modal, Alert, Button, Typography, Row, Col } from "antd";
import useCustomerReviewStore from "../../stores/useCustomerReviewStore";

// BUG-2370: the "please check before saving" popup for customer create / update,
// and the "was it really saved" result after a B2B save. Mounted once in App.js;
// customer.js talks to it through useCustomerReviewStore.
// The review is drawn like the Add / Edit Customer drawer -- same tabs, groups and
// fields, two per row -- with every value read-only and an empty field left blank.
const tabTitleStyle = { fontWeight: 600, fontSize: 16, color: "#2f3856", borderBottom: "2px solid #2f3856", paddingBottom: 4, margin: "14px 0 10px" };
const groupTitleStyle = { fontWeight: 600, fontSize: 14, color: "#0F0718", margin: "6px 0 8px" };
const noteStyle = { fontWeight: 400, fontSize: 13, color: "#595959", marginLeft: 12 };
const labelStyle = { fontWeight: 600, fontSize: 13, color: "#0F0718", marginBottom: 4 };
const valueStyle = {
  minHeight: 36,
  padding: "7px 11px",
  border: "1px solid #d9d9d9",
  borderRadius: 4,
  background: "#f5f5f5",
  color: "#0F0718",
  wordBreak: "break-word",
  whiteSpace: "pre-wrap",
};
const changedValueStyle = { ...valueStyle, background: "#fff7e6", borderColor: "#faad14", fontWeight: 600 };
const wasStyle = { fontSize: 12, color: "#8c8c8c", marginTop: 2, wordBreak: "break-word" };
// One scroll area for the whole popup, and only when the details do not fit.
const bodyStyle = { maxHeight: "62vh", overflowY: "auto", overflowX: "hidden", paddingRight: 8 };

const CustomerReviewHost = () => {
  const phase = useCustomerReviewStore((s) => s.phase);
  const review = useCustomerReviewStore((s) => s.review);
  const result = useCustomerReviewStore((s) => s.result);
  const answer = useCustomerReviewStore((s) => s.answer);
  const close = useCustomerReviewStore((s) => s.close);

  useEffect(() => {
    const { registerHost, unregisterHost, answer: answerNow } = useCustomerReviewStore.getState();
    registerHost();
    return () => {
      unregisterHost();
      // An unmount while the popup is open must not leave the save waiting forever.
      if (useCustomerReviewStore.getState()._resolve) answerNow(false);
    };
  }, []);

  const isCreate = review?.mode === "create";
  const typeText = review?.after?.b2b || "";
  const sections = review?.sections || [];

  const renderField = (field) => {
    const changed = !isCreate && field.changed;
    return (
      <Col span={12} key={field.key} className="customer-review-field" data-changed={changed ? "Y" : "N"}>
        <div style={labelStyle}>{field.label}</div>
        <div className="customer-review-value" style={changed ? changedValueStyle : valueStyle}>
          {field.value || ""}
        </div>
        {changed && <div style={wasStyle}>Was: {field.before || "(empty)"}</div>}
      </Col>
    );
  };

  return (
    <>
      <Modal
        open={phase === "review" && !!review}
        title={isCreate ? "Check the new customer before saving" : "Check the customer changes before saving"}
        closable={false}
        maskClosable={false}
        keyboard={false}
        width="62%"
        centered
        zIndex={2000}
        footer={[
          <Button key="back" id="customer-review-back" onClick={() => answer(false)}>
            Go back and edit
          </Button>,
          <Button key="ok" id="customer-review-confirm" type="primary" onClick={() => answer(true)}>
            Confirm and save
          </Button>,
        ]}
      >
        <div id="customer-review-body" style={bodyStyle}>
          {typeText && (
            <Typography.Paragraph id="customer-review-type" style={{ marginBottom: 8 }}>
              {isCreate ? "Saving as: " : "Will be saved as: "}
              <Typography.Text strong>{typeText}</Typography.Text>
            </Typography.Paragraph>
          )}
          {(review?.warnings || []).map((w) => (
            <Alert key={w} type="warning" showIcon message={w} style={{ marginBottom: 8 }} />
          ))}
          {sections.map((section) => (
            <div key={section.key} className="customer-review-section">
              <div style={tabTitleStyle}>{section.title}</div>
              {section.groups.map((group) => (
                <div key={group.key} className="customer-review-group" style={{ marginBottom: 6 }}>
                  {(group.title || group.note) && (
                    <div style={groupTitleStyle}>
                      {group.title}
                      {group.note && <span style={group.title ? noteStyle : { ...noteStyle, marginLeft: 0 }}>{group.note}</span>}
                    </div>
                  )}
                  {!isCreate && group.was && <div style={{ ...wasStyle, marginBottom: 6 }}>Was: {group.was}</div>}
                  <Row gutter={[16, 10]}>{group.fields.map(renderField)}</Row>
                </div>
              ))}
            </div>
          ))}
          {!isCreate && (
            <Typography.Text type="secondary" style={{ display: "block", marginTop: 12 }}>
              Highlighted fields will change. The old value is shown under each one.
            </Typography.Text>
          )}
        </div>
      </Modal>
      <Modal
        open={phase === "result" && !!result}
        title={result?.status === "saved" ? "B2B customer saved" : result?.status === "not_saved" ? "B2B details were NOT saved" : "Could not confirm the save"}
        zIndex={2000}
        onCancel={close}
        footer={[
          <Button key="ok" id="customer-review-result-ok" type="primary" onClick={close}>
            OK
          </Button>,
        ]}
      >
        {result?.status === "saved" && <Alert type="success" showIcon message="The server holds this B2B customer with the invoicing and delivery addresses just saved." />}
        {result?.status === "not_saved" && (
          <Alert type="error" showIcon message={result.reason} description="Do not complete a B2B bill for this customer. Open the customer again, check the details and save again." />
        )}
        {result?.status === "unknown" && <Alert type="info" showIcon message={result.reason} />}
      </Modal>
    </>
  );
};

export default CustomerReviewHost;
